/**
 * WorkQueue — master/detail layout primitive used by Triage and Approvals.
 * Left rail is a dense scrollable list; right pane shows the selected
 * item's detail. Keyboard nav: ↑/↓ or J/K to move, Esc to clear.
 */
import { ReactNode, useEffect, useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { SurfaceState } from "./SurfaceState";

export interface WorkQueueItem {
  id: string;
  /** Compact row body — already styled by the caller. */
  render: ReactNode;
}

interface WorkQueueProps {
  items: WorkQueueItem[];
  loading?: boolean;
  error?: unknown;
  isError?: boolean;
  onRetry?: () => void;
  emptyLabel?: string;
  emptyTitle?: string;
  selectedId?: string | null;
  onSelect?: (id: string | null) => void;
  renderDetail: (id: string | null) => ReactNode;
  toolbar?: ReactNode;
  className?: string;
}

export function WorkQueue({
  items,
  loading,
  error,
  isError,
  onRetry,
  emptyLabel = "Nothing to triage.",
  emptyTitle = "Queue is clear",
  selectedId: controlledSelectedId,
  onSelect,
  renderDetail,
  toolbar,
  className,
}: WorkQueueProps) {
  const [internalId, setInternalId] = useState<string | null>(null);
  const selectedId =
    controlledSelectedId !== undefined ? controlledSelectedId : internalId;
  const hasError = isError ?? Boolean(error);

  const select = useCallback(
    (id: string | null) => {
      if (onSelect) onSelect(id);
      else setInternalId(id);
    },
    [onSelect],
  );

  // Default selection: first item once data arrives.
  useEffect(() => {
    if (!selectedId && items.length > 0) select(items[0].id);
    if (selectedId && !items.find((i) => i.id === selectedId)) {
      select(items[0]?.id ?? null);
    }
  }, [items, selectedId, select]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) return;
      if (!items.length) return;
      const idx = items.findIndex((i) => i.id === selectedId);
      if (e.key === "j" || e.key === "ArrowDown") {
        e.preventDefault();
        select(items[Math.min(items.length - 1, idx + 1)].id);
      } else if (e.key === "k" || e.key === "ArrowUp") {
        e.preventDefault();
        select(items[Math.max(0, idx - 1)].id);
      } else if (e.key === "Escape") {
        select(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [items, selectedId, select]);

  return (
    <div
      className={cn(
        "grid gap-0 border border-border rounded overflow-hidden bg-card",
        "grid-cols-1 lg:grid-cols-[minmax(0,420px)_1fr]",
        "h-[calc(100vh-260px)] min-h-[520px]",
        className,
      )}
    >
      {/* Master */}
      <div className="flex flex-col border-r border-border min-w-0">
        {toolbar && (
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/30">
            {toolbar}
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4">
              <SurfaceState variant="loading" skeleton="list" />
            </div>
          ) : hasError ? (
            <SurfaceState
              variant="error"
              error={error}
              onRetry={onRetry}
              size="panel"
            />
          ) : items.length === 0 ? (
            <SurfaceState
              variant="empty"
              title={emptyTitle}
              description={emptyLabel}
              icon={<span className="text-base text-success">✓</span>}
              size="panel"
            />
          ) : (
            items.map((it) => (
              <button
                key={it.id}
                onClick={() => select(it.id)}
                className={cn(
                  "w-full text-left px-3 py-2.5 border-b border-border/60 transition-colors",
                  "hover:bg-muted/50",
                  selectedId === it.id
                    ? "bg-accent/40 border-l-2 border-l-primary"
                    : "border-l-2 border-l-transparent",
                )}
              >
                {it.render}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Detail */}
      <div className="overflow-y-auto bg-background min-w-0">
        {renderDetail(selectedId)}
      </div>
    </div>
  );
}
